import { InputOutputData, Loader } from "dash-styled"
import { FC, useMemo } from "react"
import type { PrelabelResponse, SchemaFormData } from "resha"
import {
    AnnotationOutputSchemaItem,
    convertSchemaItemToSchemaForm,
    extractDataFromSchemaItem,
    isSchemaForm,
    modernizeSchema,
    taskInputModernizeOptions,
    taskOutputModernizeOptions
} from "universal-schema/lib"
import { UniversalSchemaType } from "universal-schema/lib/0.0.4"
import { APIAppInstanceModel } from "../../../../../../../../Models/API/AppInstanceModel"
import {
    APITaskDetailsModel,
    TaskStatus
} from "../../../../../../../../Models/API/TaskModel"
import { findAnnotationOutput } from "../../../../../../../../Models/Client/AnnotationOutputSchemaItem"
import { PartialBy } from "../../../../../../../../Models/Utility/PartialBy"
import { ActionButton } from "../../../../../../../Common/ActionButton/ActionButton"
import { Fallback } from "../../../../../../../Common/DataRender/Fallback/Fallback"
import { InputFormRender } from "../../../../../../../Common/InputFormRender/InputFormRender"
import { AnnotationViewer } from "../../../../../../../Common/Lazy/AnnotationViewer"
import { CodeBlock } from "../../../../../Common/CodeBlock/CodeBlock"
import styles from "./TaskDetailInputOutput.module.css"

interface TaskDetailInputOutputProps {
    instance: APIAppInstanceModel
    taskDetails: APITaskDetailsModel
    isJsonShown: boolean
    prelabels: PrelabelResponse
    urlSigner: (url: string) => Promise<string>
}

type TaskIoData =
    | {
          type: "annotation"
          output: PartialBy<AnnotationOutputSchemaItem, "schema_instance">
      }
    | {
          type: "labeling"
          input: SchemaFormData | unknown
          output: SchemaFormData | unknown
      }

/**
 * @deprecated
 */
export const TaskDetailInputOutput: FC<TaskDetailInputOutputProps> = ({
    instance,
    taskDetails,
    isJsonShown,
    prelabels,
    urlSigner
}) => {
    const ioData = useMemo<TaskIoData | undefined>(() => {
        const res = taskDetails.response

        if (res !== undefined) {
            const [schema, data] = isSchemaForm(res)
                ? [res, res.formData]
                : Array.isArray(res)
                ? [
                      modernizeSchema({ $ref: res[0].type }),
                      res[0].schema_instance
                  ]
                : [res]
            const annotationData = isSchemaForm(schema)
                ? findAnnotationOutput(schema, taskDetails.status, data)
                : undefined

            if (annotationData !== undefined) {
                return { type: "annotation", output: annotationData }
            }
        }

        return {
            type: "labeling",
            input: isSchemaForm(taskDetails.input)
                ? taskDetails.input
                : Array.isArray(taskDetails.input)
                ? {
                      ...convertSchemaItemToSchemaForm(
                          taskDetails.input,
                          taskInputModernizeOptions
                      ),
                      formData: extractDataFromSchemaItem(taskDetails.input)
                  }
                : taskDetails.input,
            output: isSchemaForm(res)
                ? res
                : Array.isArray(res ?? [])
                ? {
                      ...convertSchemaItemToSchemaForm(
                          res ?? [],
                          taskOutputModernizeOptions
                      ),
                      formData: extractDataFromSchemaItem(res)
                  }
                : res ?? {}
        }
    }, [taskDetails])
    const layout = useMemo(
        () =>
            !isJsonShown &&
            ioData?.type === "annotation" &&
            ioData.output.type !== UniversalSchemaType.TOKEN_ANNOTATION
                ? "fit-container"
                : "stacked",
        [isJsonShown, ioData]
    )
    const renderOutput = (data: SchemaFormData | unknown): JSX.Element => {
        switch (taskDetails.status) {
            case TaskStatus.COMPLETED:
                return isSchemaForm(data) ? (
                    <InputFormRender
                        key={`${taskDetails.id}_output`}
                        {...data}
                        formData={(data as SchemaFormData).formData ?? null}
                        skipMissingDataField={false}
                        enableImageTools={true}
                        urlSigner={urlSigner}
                    />
                ) : (
                    <Fallback
                        value={data}
                        infoMessage="This output does not support pretty-rendering."
                        helpText="The DataProgram or AI need to provide schema compliant data."
                    />
                )

            case TaskStatus.CANCELED:
                return (
                    <ActionButton
                        hideButton={true}
                        text={`This task was canceled`}
                        action={() => {}}
                        buttonText={""}
                        disabled={true}
                    />
                )
            case TaskStatus.IN_PROGRESS:
                return (
                    <ActionButton
                        hideButton={true}
                        text={`This task is in progress`}
                        action={() => {}}
                        buttonText={""}
                        disabled={true}
                    />
                )
            case TaskStatus.SCHEDULED:
                return (
                    <ActionButton
                        hideButton={true}
                        text={`This task is scheduled`}
                        action={() => {}}
                        buttonText={""}
                        disabled={true}
                    />
                )
        }
    }

    return (
        <div
            data-testid="task-detail-input-output"
            className={`relative flex-grow pb-4 ${
                layout === "fit-container" ? styles.Layout__fit_container : ""
            }`}
        >
            {ioData === undefined ? null : ioData.type === "annotation" ? (
                ioData.output.schema_instance === undefined ? null : (
                    <div className={isJsonShown ? "hidden" : "h-full"}>
                        <AnnotationViewer
                            mode="readOnly"
                            key={taskDetails.id}
                            showVideoFramePane={true}
                            appParams={instance.appParams}
                            output={ioData.output as AnnotationOutputSchemaItem}
                            urlSigner={urlSigner}
                            prelabels={prelabels}
                            prelabelsInitialVisibility={
                                taskDetails.status !== TaskStatus.COMPLETED
                            }
                            instructions={
                                (isSchemaForm(taskDetails.input)
                                    ? taskDetails.input.formData
                                    : taskDetails.input[0]?.schema_instance ??
                                      "") as string
                            }
                            fallback={
                                <Loader
                                    size="xxxl"
                                    absolute={true}
                                    mask={false}
                                />
                            }
                        />
                    </div>
                )
            ) : (
                <InputOutputData
                    className={isJsonShown ? "hidden" : "h-full"}
                    inputs={
                        isSchemaForm(ioData.input) ? (
                            <InputFormRender
                                key={`${taskDetails.id}_inputput`}
                                {...ioData.input}
                                formData={
                                    (ioData.input as SchemaFormData).formData ??
                                    null
                                }
                                skipMissingDataField={false}
                                enableImageTools={true}
                                urlSigner={urlSigner}
                            />
                        ) : (
                            <Fallback
                                value={ioData.input}
                                infoMessage="This input does not support pretty-rendering."
                                helpText="The DataProgram or AI need to provide schema compliant data."
                            />
                        )
                    }
                    outputs={renderOutput(ioData.output)}
                />
            )}
            <InputOutputData
                className={isJsonShown ? "h-full" : "hidden"}
                inputs={
                    <CodeBlock className="h-full">
                        {isSchemaForm(taskDetails.input)
                            ? taskDetails.input.formData
                            : taskDetails.input}
                    </CodeBlock>
                }
                outputs={
                    taskDetails.response === undefined ? (
                        <></>
                    ) : (
                        <CodeBlock className="h-full">
                            {isSchemaForm(taskDetails.response)
                                ? taskDetails.response.formData
                                : taskDetails.response}
                        </CodeBlock>
                    )
                }
                fitToContainerHeight={true}
            />
        </div>
    )
}
